import React, { useEffect, useState, memo, useRef } from "react";
import {
    TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogTitle, DialogActions, DialogContent, InputAdornment,Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import  InfoButton  from "../../../components/Tooltip/InfoTextField";
import { CountryViewModel } from "../../../Types/ExportTypes";
import DefaultActions from "../../../components/Card/DefaultActions";
import { TemplateCustomerAutoComplete, LanguageAutoComplete, CurrencyAutoComplete, RegionAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const CountryEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { t } = useTranslation();
    const { code = "" } = params || {};
    const [isLoading, setIsLoading] = useState(false);
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { goBack, navigate } = useNavigation();
    const [viewModel, setViewModel] = useState<(CountryViewModel & { hasChanges?: boolean; originalValues?: Partial<CountryViewModel> }) | null>(null);
    const [showSettings, setShowSettings] = useState(false);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
    const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    useEffect(() => {
        if (code) {
            setIsLoading(true);
            authService
                .get<CountryViewModel>(`/settingsApi/GetCountry?code=${code}`)
                .then((response) =>
                    setViewModel(
                        response
                            ? { ...response, originalValues: { ...response }, hasChanges: false }
                            : null
                    )
                )
                .catch((error: any) => {
                    showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
                })
                .finally(() => setIsLoading(false));
        } else {
     
            const defaultCountry: CountryViewModel & { originalValues: Partial<CountryViewModel>; hasChanges: boolean } = {
                code: "",
                name: "",
                createNew: true,
                originalValues: {},
                hasChanges: false,
            };
            defaultCountry.originalValues = { ...defaultCountry };
            setViewModel(defaultCountry);
        }
    }, [code, authService]);

    const handleSave = async () => {
        if (!viewModel) return;
        if (!validateCountry()) return; // Prevent save if validation fails
        var newItem = viewModel.createNew ?? false;

        setIsLoading(true);
        try {
            const response = await authService.post<CountryViewModel>("/settingsApi/CreateOrUpdateCountry", viewModel);
            setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
            showSnackbar(t("dialog.savedSuccessfully"), "success");

            if (newItem) {
                navigate({ path: "/settings/country/edit", params: { code: response.code } });
            }
        } catch {
            showSnackbar(t("dialog.saveErrorGeneric"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (field: keyof CountryViewModel, value: any) => {
        setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
    };

    const handleCancelChanges = () => {
        setViewModel((prev) => {
            if (!prev || !prev.originalValues) return prev;
            const original = prev.originalValues as CountryViewModel;
            return { ...original, originalValues: { ...original }, hasChanges: false };
        });
    };

    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };


    const handleCopy = async (newCode?: string) => {
      
        var newItem = viewModel?.createNew ?? false;
        if (newItem) {
            showSnackbar(t("dialog.copyPreliminaryCountry"), "warning");
            return;
        }

        if (newCode && newCode === viewModel?.code) {
            showSnackbar(t("dialog.countryCodeSameAsSource"), "warning");
            return;
        }
        // If newCode is provided, update viewModel and continue
        if (newCode) {
            setViewModel(prev => ({ ...prev, newCode }));
        }


        // If newCode isn't set, open the modal and return
        if (!viewModel?.newCode && !newCode) {
            setIsCopyModalOpen(true);
            return;
        }
        setIsCopyModalOpen(false);
        setIsLoading(true);
        try {
            var response = await authService.post<CountryViewModel>(`/settingsApi/CopyCountry`, { ...viewModel, newCode });
            setViewModel(response);
            showSnackbar(t("dialog.copySuccess"), "success");

            navigate({ path: "/settings/country/edit", params: {  code: response.code } });

        } catch (error: any) {

            showSnackbar(`${t("dialog.copyError")} : ${error.response?.data ?? error.message}`, "error");
            setViewModel(prev => ({ ...prev, newCode: null }));

        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
       
        const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
        if (!isConfirmed) return;
        var newItem = viewModel?.createNew ?? false;
        if (newItem) {
            navigate({ path: "/settings/country/list" });
            return;
        }

        setIsLoading(true);
        try {
            await authService.post(`/settingsApi/DeleteCountry`, viewModel);
            showSnackbar(t("dialog.deletedSuccessfully"), "success");

            navigate({ path: "/settings/country/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setIsLoading(false);
        }
    };

    const validateCountry = () => {
        if (!viewModel) {
            showSnackbar(t("dialog.dataMissing"), "error");
            return false;
        }

        if (!viewModel.code || viewModel.code.trim() === "") {
            showSnackbar(t("help.codeRequired"), "warning");
            return false;
        }

        if (!viewModel.name || viewModel.name.trim() === "") {
            showSnackbar(t("help.nameRequired"), "warning");
            return false;
        }


        return true;
    };





    type CodeInputModalProps = {
        open: boolean;
        onClose: () => void;
        onConfirm: (newCode: string) => void;
        ViewModel?: CountryViewModel | null
    };
    const CodeInputModal = memo(({ open, onClose, onConfirm, ViewModel }: CodeInputModalProps) => {
        const [tempCode, setTempCode] = useState("");
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogTitle>{t("dialog.enterNewCode")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("common.newCode")}
                        fullWidth
                        variant="outlined"
                        value={tempCode}
                        onChange={(e) => setTempCode(e.target.value)} // Only updates local state
                        error={ViewModel?.code === tempCode}
                        helperText={ViewModel?.code === tempCode ? t("help.countryCodeSameAsSource") : ""}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => {
                            if (!tempCode.trim()) {
                                showSnackbar(t("dialog.codeEmpty"), "warning")
                                return;
                            }
                            onConfirm(tempCode?.trim()); // Pass only on confirm
                        }}
                        color="primary"
                    >
                     {t("common.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    });
    return (
        <>
        
            <CenteredCard
                ref={toolbarRef}
                title={t("common.edit") + " " + t("common.country")}
                isLoading={isLoading}
                handleSave={handleSave}
                handleCancel={handleCancelChanges}
                hasChanges={viewModel?.hasChanges ?? false}
            >
                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    maxWidth: "fit-content",
                    mb: 2
                }}>
                    <DefaultActions
                        onSave={handleSave}
                        onDelete={handleDelete}
                        onCopy={handleCopy}
                    />
                </Box>

                <CodeInputModal open={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onConfirm={handleCopy} ViewModel={viewModel} />
                {viewModel && (
                    <>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                    label={t("common.code") }
                                value={viewModel.code || ""}
                                onChange={(e) => handleChange("code", e.target.value)}
                                fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 10 }}
                                    disabled={viewModel.createNew != true}
                                    error={!viewModel.code || viewModel.code.trim() === ""}
                                    helperText={!viewModel.code || viewModel.code.trim() === "" ? t("help.codeRequired") : ""}
                            />
                        </Grid>
                  
                        <Grid item xs={12}>
                            <TextField
                                    label={t("common.name")}
                                value={viewModel.name || ""}
                                onChange={(e) => handleChange("name", e.target.value)}
                                fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 50 }}
                                    error={!viewModel.name || viewModel.name.trim() === ""}
                                    helperText={!viewModel.name || viewModel.name.trim() === "" ?  t("help.nameRequired")  : ""}
                            />
                        </Grid>
                            <Grid item xs={12}>
                          
                                <FormControl fullWidth margin="normal">
                                    <RegionAutoComplete
                                        key={viewModel.region || null}
                                        valueId={viewModel.region || null}
                                        onChange={(selected) => handleChange("region", selected?.value || "")}
                                        infoText={t("help.region")}

                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <LanguageAutoComplete
                                        key={viewModel.languageCode || null}
                                        valueId={viewModel.languageCode || null}
                                        onChange={(selected) => handleChange("languageCode", selected?.value || "")}
                                        infoText={t("help.defaultCustomerLanguage")}

                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <CurrencyAutoComplete
                                        key={viewModel.currencyCode || null}
                                        valueId={viewModel.currencyCode || null}
                                        onChange={(selected) => handleChange("currencyCode", selected?.value || "")}
                                        infoText={t("help.defaultCustomerCurrency")}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <TemplateCustomerAutoComplete
                                        key={viewModel.custTemplateNo || null}
                                        valueId={viewModel.custTemplateNo || null}
                                        onChange={(selected) => handleChange("custTemplateNo", selected?.value || "")}
                                        label={t("common.templateCustomerB2C")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Switch checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />}
                                    label={t("common.showAdvancedSettings")}
                                />
                            </Grid>

                            {showSettings && (
                                <>
                                    <Box
                                        sx={{
                                            backgroundColor: "secondary.main",
                                            padding: 2,
                                            borderRadius: 2,
                                            width: "100%"
                                        }}
                                    >


                                        <Grid item xs={12}>
                                            <TextField
                                                label={t("common.vatNo")}
                                                value={viewModel.vatNo || ""}
                                                onChange={(e) => handleChange("vatNo", e.target.value)}
                                                fullWidth
                                                margin="normal"
                                                inputProps={{ maxLength: 50 }}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                label={t("common.callingCode")}
                                                value={viewModel.callingCode || ""}
                                                onChange={(e) => handleChange("callingCode", e.target.value)}
                                                fullWidth
                                                margin="normal"
                                                inputProps={{ maxLength: 10 }}
                                            />
                                        </Grid>

                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={<Checkbox checked={!!viewModel.ocr} onChange={(e) => handleChange("ocr", e.target.checked)} />}
                                                label={t("common.createOcr")}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={<Checkbox checked={!!viewModel.customsInvoice} onChange={(e) => handleChange("customsInvoice", e.target.checked)} />}
                                                label={t("common.customsInvoice")}
                                            />
                                        </Grid>

                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={<Checkbox checked={!!viewModel.unifaunReturnDocument} onChange={(e) => handleChange("unifaunReturnDocument", e.target.checked)} />}
                                                label={t("common.createReturnDocument")}
                                            />
                                        </Grid>

                                    </Box>
                                </>
                            )}
                    </Grid>
        

                    </>
                )}

                   
          <Box sx={{ marginTop: 2, textAlign: "center" }}>
            <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
              {t("common.save")}
            </Button>
            <Button variant="outlined" onClick={goBack}>
              {t("common.back")}
            </Button>
          </Box>
            </CenteredCard>

       
        </>
    );
};

export default CountryEditPage;
